import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const EMPTY = { name: "", role: "", specialties: "", photo: "", active: true };

export default function Stylists() {
  const [stylists, setStylists] = useState([]);
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(() => api.get("/stylists").then((r) => setStylists(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ ...s, specialties: (s.specialties || []).join(", "), photo: s.photo || "" });
    setOpen(true);
  };

  const toggleSpec = (name) => {
    const arr = form.specialties ? form.specialties.split(",").map((x) => x.trim()).filter(Boolean) : [];
    const next = arr.includes(name) ? arr.filter((x) => x !== name) : [...arr, name];
    setForm({ ...form, specialties: next.join(", ") });
  };

  const save = async () => {
    const payload = {
      name: form.name,
      role: form.role,
      photo: form.photo || null,
      active: true,
      specialties: form.specialties ? form.specialties.split(",").map((x) => x.trim()).filter(Boolean) : [],
    };
    try {
      if (editing) await api.put(`/admin/stylists/${editing.id}`, payload);
      else await api.post("/admin/stylists", payload);
      toast.success(editing ? "Empleado actualizado" : "Empleado creado");
      setOpen(false);
      load();
    } catch {
      toast.error("No se pudo guardar");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/stylists/${id}`);
      toast.success("Empleado eliminado");
      load();
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const selected = form.specialties ? form.specialties.split(",").map((x) => x.trim()) : [];

  return (
    <div data-testid="admin-stylists" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif-display text-3xl">Empleados</h1>
          <p className="text-sm text-muted-foreground">{stylists.length} miembros</p>
        </div>
        <button
          data-testid="stylist-new-btn"
          onClick={openNew}
          className="flex items-center gap-2 bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-gold-dark"
        >
          <Plus size={16} /> Nuevo
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {stylists.map((s) => (
          <div key={s.id} data-testid={`stylist-admin-${s.id}`} className="flex items-center gap-4 border border-border bg-card p-5">
            <img src={s.photo} alt={s.name} className="h-16 w-16 rounded-full object-cover" />
            <div className="flex-1">
              <h3 className="font-serif-display text-xl">{s.name}</h3>
              <p className="text-sm text-primary">{s.role}</p>
              <p className="mt-1 text-xs text-muted-foreground">{(s.specialties || []).length} especialidades</p>
            </div>
            <div className="flex flex-col gap-2">
              <button data-testid={`stylist-edit-${s.id}`} onClick={() => openEdit(s)} className="text-muted-foreground hover:text-primary">
                <Pencil size={15} />
              </button>
              <button data-testid={`stylist-delete-${s.id}`} onClick={() => remove(s.id)} className="text-muted-foreground hover:text-destructive">
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="stylist-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-display text-2xl">
              {editing ? "Editar empleado" : "Nuevo empleado"}
            </DialogTitle>
            <DialogDescription>Completa la información del empleado.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nombre</Label>
              <Input data-testid="stylist-input-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label>Puesto</Label>
              <Input data-testid="stylist-input-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="Estilista, Barbero..." />
            </div>
            <div>
              <Label>Foto (URL)</Label>
              <Input value={form.photo} onChange={(e) => setForm({ ...form, photo: e.target.value })} />
            </div>
            <div>
              <Label>Especialidades</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {services.map((sv) => (
                  <button
                    key={sv.id}
                    type="button"
                    onClick={() => toggleSpec(sv.name)}
                    className={`border px-3 py-1 text-xs transition-colors ${
                      selected.includes(sv.name) ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"
                    }`}
                  >
                    {sv.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <button
              data-testid="stylist-save-btn"
              onClick={save}
              className="bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-gold-dark"
            >
              Guardar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
